import React from 'react';
import { useRetentionMetrics, RenewalPoint } from '../use-cases/useRetentionMetrics';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('ko-KR').format(amount || 0) + '원';

const formatMonthLabel = (month: string) => `${Number(month.split('-')[1])}월`;

const rateColor = (rate: number | null) => {
  if (rate === null) return 'text-gray-400';
  if (rate >= 80) return 'text-green-600';
  if (rate >= 60) return 'text-amber-600';
  return 'text-red-600';
};

/** 대기가 남은 달은 갱신율이 아직 확정이 아니다 — 숫자만 크게 띄우면 원장이 오해한다 */
function RateValue({ point }: { point: RenewalPoint }) {
  if (point.renewal_rate === null) {
    return <span className="text-2xl font-bold text-gray-400">–</span>;
  }
  return (
    <span className={`text-2xl font-bold ${rateColor(point.renewal_rate)}`}>
      {point.renewal_rate}%
      {!point.settled && (
        <span className="ml-2 align-middle px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
          집계 중
        </span>
      )}
    </span>
  );
}

export default function RetentionPanel({ month }: { month: string }) {
  const { data, loading, error } = useRetentionMetrics(month);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
        {error}
      </div>
    );
  }
  if (loading && !data) {
    return (
      <div className="bg-white rounded-lg shadow p-5 text-center text-gray-400">
        갱신 지표 불러오는 중...
      </div>
    );
  }
  if (!data) return null;

  const { renewal, capacity, upcoming } = data;
  const occ = capacity.occupancy_rate;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* 갱신율 */}
        <div className="bg-white p-5 rounded-lg shadow">
          <p className="text-sm text-gray-500">이 달 갱신율</p>
          <p className="mt-1">
            <RateValue point={renewal} />
          </p>
          <p className="mt-1 text-xs text-gray-500">
            만료 {renewal.expired}명 중 재등록 {renewal.renewed} · 이탈{' '}
            <b className="text-red-600">{renewal.churned}</b>
            {renewal.pending > 0 && ` · 대기 ${renewal.pending}`}
          </p>
          {renewal.trial_expired > 0 && (
            <p className="mt-0.5 text-xs text-gray-400">
              체험권 만료 {renewal.trial_expired}건은 집계에서 제외
            </p>
          )}
        </div>

        {/* 좌석 가동률 */}
        <div className="bg-white p-5 rounded-lg shadow">
          <p className="text-sm text-gray-500">좌석 가동률</p>
          <p
            className={`mt-1 text-2xl font-bold ${
              occ === null ? 'text-gray-400' : occ >= 95 ? 'text-red-600' : 'text-gray-900'
            }`}
          >
            {occ === null ? '–' : `${occ}%`}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {capacity.active_members}명 / 좌석 {capacity.seats}석 · 빈자리{' '}
            {capacity.empty_seats}석
          </p>
          {occ !== null && occ >= 95 && (
            <p className="mt-0.5 text-xs text-red-600">
              만석 — 매출을 늘리려면 단가나 회전을 봐야 합니다
            </p>
          )}
        </div>

        {/* 다음 달 예상 */}
        <div className="bg-white p-5 rounded-lg shadow">
          <p className="text-sm text-gray-500">
            다음 달({formatMonthLabel(upcoming.month)}) 만료 예정
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{upcoming.count}명</p>
          <p className="mt-1 text-xs text-gray-500">
            직전 결제액 합계 {formatCurrency(upcoming.amount)}
          </p>
          {upcoming.forecast_amount !== null ? (
            <p className="mt-0.5 text-xs text-blue-600">
              최근 갱신율 {data.avg_renewal_rate}% 적용 시 약{' '}
              <b>{formatCurrency(upcoming.forecast_amount)}</b>
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-gray-400">
              확정된 갱신율 이력이 아직 없어 예상액은 계산하지 않습니다
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 갱신 추이 */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-1">📈 갱신 추이</h3>
          <p className="text-xs text-gray-400 mb-4">
            만료일이 지난 회원만 판정합니다. 연장은 새 이용권으로 발급되므로 회원 기준으로 셉니다.
          </p>
          {data.renewal_trend.length > 0 ? (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase">
                  <th className="px-2 py-2 text-left">월</th>
                  <th className="px-2 py-2 text-right">만료</th>
                  <th className="px-2 py-2 text-right">재등록</th>
                  <th className="px-2 py-2 text-right">이탈</th>
                  <th className="px-2 py-2 text-right">갱신율</th>
                </tr>
              </thead>
              <tbody>
                {data.renewal_trend.map((t) => (
                  <tr key={t.month} className="border-b border-gray-100">
                    <td className="px-2 py-2 text-gray-900">
                      {formatMonthLabel(t.month)}
                      {!t.settled && (
                        <span className="ml-1 text-xs text-gray-400">(집계 중)</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-600">{t.expired}</td>
                    <td className="px-2 py-2 text-right text-purple-600">{t.renewed}</td>
                    <td className="px-2 py-2 text-right text-red-600">{t.churned}</td>
                    <td className={`px-2 py-2 text-right font-semibold ${rateColor(t.renewal_rate)}`}>
                      {t.renewal_rate === null ? '–' : `${t.renewal_rate}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-400 text-center py-6">만료 이력이 없습니다.</p>
          )}
        </div>

        {/* 이탈 명단 */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            🚪 이 달 이탈 {data.churned_members.length}명
          </h3>
          <p className="text-xs text-gray-400 mb-4">
            만료일이 지났는데 재등록이 없는 회원. 금액이 큰 순으로 보여줍니다.
          </p>
          {data.churned_members.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase">
                    <th className="px-2 py-2 text-left">회원</th>
                    <th className="px-2 py-2 text-left">만료일</th>
                    <th className="px-2 py-2 text-right">직전 결제</th>
                    <th className="px-2 py-2 text-center">연락</th>
                  </tr>
                </thead>
                <tbody>
                  {data.churned_members.map((c) => (
                    <tr key={c.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-2 py-2">
                        <span className="font-medium text-gray-900">{c.name}</span>
                        {c.last_voucher && (
                          <span className="block text-xs text-gray-400">{c.last_voucher}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-gray-600">{c.end_date || '-'}</td>
                      <td className="px-2 py-2 text-right font-medium text-gray-900">
                        {formatCurrency(c.last_price)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {c.phone ? (
                          <a
                            href={`tel:${c.phone}`}
                            className="px-2 py-1 rounded bg-blue-50 text-blue-600 text-xs hover:bg-blue-100"
                          >
                            📞 {c.phone}
                          </a>
                        ) : (
                          <span className="text-xs text-gray-300">번호 없음</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-400 text-center py-6">이 달 이탈한 회원이 없습니다.</p>
          )}
        </div>
      </div>
    </div>
  );
}
